import { Step, StepLabel, Stepper } from "@mui/material";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import SkipIcon from "../../../../static/assets/images/icon-skip-right-arrow.svg";
import AutomationIcon from "../../../../static/assets/images/icon-skip-automation.svg";
import { useActionRequest, useApiTrigger } from "../../../common/hooks/api";
import { DataRequestDetail, NetworkRequest } from "../../../common/models";
import { NetworkRequestState } from "../../../common/models/NetworkRequest";
import { Api } from "../../../common/service/Api";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { DataRequestPublicId, DataRequestState } from "../../../common/service/server/Types";
import { SubjectCard } from "../../components/dataRequest/DataRequestSubjectCard";
import { DataRequestBody } from "../../components/dataRequest/stayCompliant/DataRequestBody";
import { EditDataRequestDialog } from "../../components/dialog/EditDataRequestDialog";
import { Button as AquiloButton, useSnackBar } from "@truevault/aquilo";
import {
  DataRequestProcessingProvider,
  RequestStep,
  useDataRequestProcessingContext,
} from "../../components/dataRequest/stayCompliant/DataRequestProcessingContext";
import { PageHeader } from "../../components/layout/header/Header";
import { PageHeaderBack } from "../../components/layout/header/PageHeaderBack";
import { Error as RequestsError } from "../../copy/requests";
import {
  useOrganization,
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
  useRequest,
} from "../../hooks";
import { RouteHelpers, Routes } from "../../root/routes";
import {
  ProgressFooter,
  ProgressFooterActions,
  ProgressFooterBack,
  ProgressFooterSaveNotification,
} from "../get-compliant/ProgressFooter";
import { paginationStateToUrlSearch } from "../../../common/hooks/useStatefulPagination";
import { DataSubjectRequestDetailDto } from "../../../common/service/server/dto/DataSubjectRequestDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import {
  eligibleVendorsToContactForDeleteRequest,
  vendorsToContactForDeleteRequest,
} from "../../components/dataRequest/stayCompliant/steps/delete/ContactVendors";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { ContactVendorRequest } from "../../../common/service/server/dto/UpdateVendorRequest";
import {
  filterVendorsForOptOutNotifyThirdPartiesStep,
  filterVendorsToContactForOptOutRequest,
} from "../../components/dataRequest/stayCompliant/steps/opt-out/NotifyThirdParties";
import {
  alreadyProcessedVendorForLimitRequest,
  filterLimitVendorsToContactForRequest,
} from "../../components/dataRequest/stayCompliant/steps/limit/ContactVendors";
import { getAllInformationForLimitRequests } from "../requestInstructions/LimitInstructionsPage";
import { useDataRequestAttachments } from "../../hooks/useDataRequestAttachments";
import {
  handleAccessRequestNextStep,
  isAccessRequestRetrieveDataStep,
} from "./accessRequestNextStepHandler";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";

type MatchParams = {
  requestId: DataRequestPublicId;
};

type Props = RouteComponentProps<MatchParams>;
type RequestDetailLocationState = {
  pagination?: any;
  fromRecordLookup?: boolean;
};

type ContactRequestorTriggerOptions = {
  closeAfterSend?: boolean;
  currentRequestState?: DataRequestState;
  attachments?: File[];
};

export const Detail = ({ match }: Props) => {
  const history = useHistory();
  const primaryOrgId = usePrimaryOrganizationId();

  const [org, organizationRequest, refreshOrganization] = useOrganization(primaryOrgId);
  const [request, requestRequest, refreshRequest, updateVendorOutcomeMutation] = useRequest(
    primaryOrgId,
    match.params.requestId,
  );
  const [vendors, vendorRequest, refreshVendors] = useOrganizationVendors(
    primaryOrgId,
    undefined,
    true,
  );

  const [everRendered, setEverRendered] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [submitValues, setSubmitValues] = useState<any>(null);

  const SUBMIT_FIELD_NAMES = [
    "requestDate",
    "subjectEmailAddress",
    "subjectMailingAddress",
    "subjectPhoneNumber",
    "subjectFirstName",
    "subjectLastName",
    "dataRequestTypeId",
    "submissionMethod",
    "notice",
  ];

  const handleSubmit = (values) => {
    setSubmitValues(Object.assign({}, values));
  };

  const {
    fetch: triggerUpdate,
    request: updateRequestState,
    resetState: resetUpdateRequest,
  } = useActionRequest({
    api: () => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const processedSubmitValues = Object.fromEntries(
        Object.entries(submitValues)
          .filter(([k]) => SUBMIT_FIELD_NAMES.includes(k))
          .map(([k, v]) => {
            return k === "requestDate" && typeof (v as any).toISOString == "function"
              ? [k, (v as any).toISOString()]
              : [k, v];
          }),
      );
      const values = Object.assign({}, existingValues, processedSubmitValues) as any;
      return Api.dataRequest.updateRequest(primaryOrgId, request.id, values);
    },
    messages: { defaultError: RequestsError.UpdateRequest },
  });

  // make the request whenever submit values are set
  useEffect(() => {
    if (submitValues) {
      triggerUpdate();
      setSubmitValues(null);
    }
  }, [triggerUpdate, submitValues]);

  // Reset dialog state when request is updated successfully
  useEffect(() => {
    if (updateRequestState.success && showEditDialog) {
      setShowEditDialog(false);
      // Reset the request state to allow future operations
      resetUpdateRequest();
    }
  }, [updateRequestState.success, showEditDialog, resetUpdateRequest]);

  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refreshOrganization(), refreshRequest(), refreshVendors()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshOrganization, refreshRequest, refreshVendors]);

  const forwardApi = useCallback(
    (overrideState?: DataRequestState) =>
      Api.dataRequest.stepRequestForward(primaryOrgId, request.id, overrideState ?? request.state),
    [primaryOrgId, request?.id, request?.state],
  );
  const [forwardStep, forwardStepRequest] = useApiTrigger(
    forwardApi,
    refreshAll,
    RequestsError.TransitionProcessing,
  );

  const backApi = useCallback(
    () => Api.dataRequest.stepRequestBack(primaryOrgId, request.id, request.state),
    [primaryOrgId, request?.id, request?.state],
  );
  const [backStep, backStepRequest] = useApiTrigger(
    backApi,
    refreshAll,
    RequestsError.TransitionProcessing,
  );

  const [isAutoSendAndClosingInProgress, setIsAutoSendAndClosingInProgress] = useState(false);

  useEffect(() => {
    if (NetworkRequest.areFinished(requestRequest, vendorRequest, organizationRequest)) {
      setEverRendered(true);
    }
  }, [requestRequest, vendorRequest, organizationRequest]);

  // Page Load
  if (!everRendered) {
    return <LoadingPageWrapper />;
  }

  if (!request) {
    return (
      <StandardPageWrapper>
        <PageHeader titleContent="Uh oh... we couldn't find that request." />
      </StandardPageWrapper>
    );
  }

  if (isAutoSendAndClosingInProgress) {
    return <LoadingPageWrapper />;
  }

  const isBatch = !!request.batchPublicId && !!request.batch;
  const locationState: RequestDetailLocationState = (history?.location?.state ??
    {}) as RequestDetailLocationState;
  const cameFromRecordLookup = locationState.fromRecordLookup === true;
  const showEditLink =
    request.submissionSource === "POLARIS" &&
    !request.isAutocreated &&
    (request.state == "VERIFY_CONSUMER" || request.state == "REMOVE_CONSUMER") &&
    !isBatch;

  const backRoute = cameFromRecordLookup
    ? Routes.records.recordLookup
    : `/requests/${request.state != "CLOSED" ? "active" : "closed"}`;
  const backLabel = cameFromRecordLookup
    ? "Record Lookup"
    : `${request.state != "CLOSED" ? "Open" : "Closed"} Requests`;
  const titleHeaderContent = <PageHeaderBack to={backRoute}>{backLabel}</PageHeaderBack>;

  const copyEmailToClipboard = async () => {
    if (request.subjectEmailAddress) {
      await navigator.clipboard.writeText(request.subjectEmailAddress);
    }
  };

  const titleContent = request.batch ? (
    `${request.batch.requestCount} requests`
  ) : (
    <span style={{ display: "inline-block", maxWidth: "440px", overflowWrap: "anywhere" }}>
      {request.subjectEmailAddress}
    </span>
  );

  const copyEmailButton =
    !request.batch && request.subjectEmailAddress ? (
      <AquiloButton size="s" color="tertiary" onClick={copyEmailToClipboard}>
        Copy
      </AquiloButton>
    ) : null;

  const viewEmailThreadButton = request.linkedPostmarkConversationId ? (
    <AquiloButton
      size="s"
      color="tertiary"
      onClick={() =>
        history.push(
          RouteHelpers.requests.privacyEmailConversation(request.linkedPostmarkConversationId!),
        )
      }
    >
      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
      View email thread
    </AquiloButton>
  ) : null;

  const headerActions =
    copyEmailButton || viewEmailThreadButton || showEditLink ? (
      <div className="tw-boundary">
        <div className="flex items-start gap-sm">
          {copyEmailButton}
          {viewEmailThreadButton}
          {showEditLink && (
            <AquiloButton
              size="s"
              color="secondary"
              onClick={() => {
                if (updateRequestState.success || updateRequestState.error) {
                  resetUpdateRequest();
                }
                setShowEditDialog(true);
              }}
            >
              Edit request
            </AquiloButton>
          )}
        </div>
      </div>
    ) : undefined;

  return (
    <DataRequestProcessingProvider
      request={request}
      vendors={vendors}
      refreshRequest={refreshRequest}
      updateVendorOutcomeMutation={updateVendorOutcomeMutation}
    >
      <StandardPageWrapper
        footer={
          <Footer
            request={request}
            forwardStep={forwardStep}
            forwardStepRequest={forwardStepRequest}
            backStep={backStep}
            backStepRequest={backStepRequest}
            isAutoSendAndClosingInProgress={isAutoSendAndClosingInProgress}
            setIsAutoSendAndClosingInProgress={setIsAutoSendAndClosingInProgress}
            isRefreshing={isRefreshing}
            refreshAll={refreshAll}
          />
        }
      >
        <div className="max-width-896">
          <PageHeader
            marginBottomPx={24}
            titleContent={titleContent}
            titleHeaderContent={titleHeaderContent}
            actions={headerActions}
          />
          <SubjectCard
            request={request}
            primaryOrgId={primaryOrgId}
            org={org}
            refreshRequest={refreshRequest}
          />
          {showEditLink && (
            <EditDataRequestDialog
              request={request}
              open={showEditDialog}
              onClose={() => {
                setShowEditDialog(false);
              }}
              onDone={() => {
                // Don't close dialog here - let the useEffect handle it after successful update
                refreshRequest();
              }}
              updateRequestState={updateRequestState}
              handleSubmit={handleSubmit}
            />
          )}
          <RequestStepper />
          <DataRequestBody request={request} />
        </div>
      </StandardPageWrapper>
    </DataRequestProcessingProvider>
  );
};

/**
 * Different icons that can be shown instead of a number in the request Stepper.
 */
enum StepIcon {
  SKIPPED,
  AUTOMATED,
}

/**
 * Return an icon component for a request step.
 * @param icon   The type of icon.
 * @param active Whether the user is looking at the step (i.e. it is the current step) or not. We need to add the active
 *               class by hand, since the stepper doesn't do it automatically for StepLabels with a <StepIconComponent>.
 *               Uses a data attribute instead of a class to prevent overriding the SVG classes added by <Stepper>.
 */
function getStepIcon(icon: StepIcon, active: boolean) {
  switch (icon) {
    case StepIcon.SKIPPED:
      return <SkipIcon data-isActiveStep={active} />;
    case StepIcon.AUTOMATED:
      return <AutomationIcon data-isActiveStep={active} />;
  }
}

const RequestStepper = () => {
  const { steps, activeStep } = useDataRequestProcessingContext();

  return (
    <>
      <Stepper activeStep={activeStep} alternativeLabel className="stepper pt-std mb-std">
        {steps.map(({ name, status, automated }, i) => (
          <Step key={name} active={i === activeStep}>
            {i < activeStep && status === "skipped" ? (
              <StepLabel StepIconComponent={() => getStepIcon(StepIcon.SKIPPED, i === activeStep)}>
                {name}
              </StepLabel>
            ) : automated ? (
              <StepLabel
                StepIconComponent={() => getStepIcon(StepIcon.AUTOMATED, i === activeStep)}
              >
                {name}
              </StepLabel>
            ) : (
              <StepLabel>{name}</StepLabel>
            )}
          </Step>
        ))}
      </Stepper>
    </>
  );
};

type ContentProps = {
  request: DataRequestDetail;
  forwardStep: any;
  forwardStepRequest: NetworkRequestState;
  backStep: any;
  backStepRequest: NetworkRequestState;
  isAutoSendAndClosingInProgress: boolean;
  setIsAutoSendAndClosingInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  isRefreshing: boolean;
  refreshAll: () => Promise<void>;
};

const Footer = ({
  request,
  forwardStep,
  forwardStepRequest,
  backStep,
  backStepRequest,
  isAutoSendAndClosingInProgress,
  setIsAutoSendAndClosingInProgress,
  isRefreshing,
  refreshAll,
}: ContentProps) => {
  const history = useHistory();
  const enqueueSnackbar = useSnackBar();
  const [org] = usePrimaryOrganization();
  const locationState: RequestDetailLocationState = (history?.location?.state ??
    {}) as RequestDetailLocationState;

  const navigateToInbox = useCallback(() => {
    enqueueSnackbar("Request Closed Successfully", {
      variant: "success",
    });
    let href = Routes.requests.active;
    if (locationState.pagination) {
      href = `${href}?${paginationStateToUrlSearch(locationState.pagination)}`;
    }
    history.push(href);
  }, [enqueueSnackbar, history]);

  const { data: dataMap } = useDataMap(org.id);
  const { answers: sellingAndSharingSurveyAnswers } = useSurvey(
    org.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const {
    managedAdNetworks,
    isLoading: managedAdNetworksLoading,
    error: managedAdNetworksError,
  } = useManagedAdNetworks(org.id);

  const {
    stepCompleted,
    nextStepBlockedReason,
    activeStep: activeStepIndex,
    steps,
    lastStep,
    updateRequests,
    attachment,
    vendors: { all: vendors },
    personalIdentifiers,
    setGeneratedZip,
    isGeneratingAccessZip,
    setIsGeneratingAccessZip,
  } = useDataRequestProcessingContext();

  // Only fetch attachments for access request types (KNOW, PORTABILITY) - saves ~300ms for other request types
  const isAccessRequest =
    request.dataRequestType?.workflowType === "KNOW" ||
    request.dataRequestType?.workflowType === "PORTABILITY";
  const { attachments } = useDataRequestAttachments(org.id, request.id, {
    enabled: isAccessRequest,
  });

  const [contactVendors] = useApiTrigger(
    (values: ContactVendorRequest) => Api.dataRequest.contactVendors(org.id, request.id, values),
    () => {},
    RequestsError.TransitionProcessing,
  );

  const [contactRequestor, contactRequestorRequest] = useApiTrigger(
    (options: ContactRequestorTriggerOptions = {}) =>
      Api.dataRequest.contactRequestor(org.id, request.id, {
        attachments: options.attachments ?? attachment?.files ?? [],
        closeAfterSend: options.closeAfterSend,
        currentRequestState: options.currentRequestState,
      }),
    refreshAll,
    RequestsError.TransitionProcessing,
  );

  const sendAndClose = lastStep && ["NOTIFY_CONSUMER", "SEND_DATA"].includes(request?.state);
  const isBatch = !!request?.batch;

  const autoSendRequestEmailNotification =
    activeStepIndex >= 0 &&
    shouldAutoSendRequestEmailNotifications(
      request,
      org,
      vendors,
      dataMap,
      sellingAndSharingSurveyAnswers,
      managedAdNetworks,
      !managedAdNetworksLoading && managedAdNetworksError == null,
      steps[activeStepIndex],
    );

  const nextButtonText = (() => {
    if (autoSendRequestEmailNotification) {
      return isBatch
        ? "Auto-send notifications & close requests"
        : "Auto-send notifications & close request";
    } else if (sendAndClose) {
      return isBatch ? "Send & close requests" : "Send & close request";
    } else if (lastStep) {
      return isBatch ? "Close requests" : "Close request";
    } else {
      return "Next step";
    }
  })();

  if (request.state === "CLOSED") {
    return null;
  }

  const isMailboxDisconnected = !org.mailbox.canSendEmail;

  // The Connected System access handoff (POL-7757) must resolve before the request can proceed.
  const isWaitingForAccessAutomation =
    request.state === "RETRIEVE_DATA" &&
    request.vendorOutcomes.some((vo) => vo.outcome === "AUTOMATION_WAITING");

  const backButtonDisabled =
    backStepRequest.running ||
    forwardStepRequest.running ||
    contactRequestorRequest.running ||
    isGeneratingAccessZip;

  const nextButtonDisabled =
    backStepRequest.running ||
    forwardStepRequest.running ||
    contactRequestorRequest.running ||
    isRefreshing ||
    (!sendAndClose && !stepCompleted) ||
    !stepCompleted;

  return (
    <ProgressFooter
      actions={
        <ProgressFooterActions
          prevContent={<ProgressFooterBack />}
          prevDisabled={backButtonDisabled}
          prevLoading={backStepRequest.running}
          prevOnClick={
            activeStepIndex === 0
              ? () => {
                  let href = locationState.fromRecordLookup
                    ? Routes.records.recordLookup
                    : Routes.requests.active; // This needs to be able to go back to active or closed
                  if (locationState.pagination) {
                    href = `${href}?${paginationStateToUrlSearch(locationState.pagination)}`;
                  }
                  history.push(href);
                }
              : () => {
                  backStep();
                }
          }
          nextContent={nextButtonText}
          nextDisabled={
            nextButtonDisabled ||
            (isMailboxDisconnected && sendAndClose) ||
            (isMailboxDisconnected && autoSendRequestEmailNotification)
          }
          nextLoading={
            forwardStepRequest.running ||
            contactRequestorRequest.running ||
            isAutoSendAndClosingInProgress ||
            isGeneratingAccessZip
          }
          nextDisabledTooltip={
            isMailboxDisconnected
              ? "Please connect your email address before continuing"
              : isWaitingForAccessAutomation
              ? "Waiting for a response from your Connected System before this request can proceed"
              : nextStepBlockedReason
          }
          nextOnClick={async () => {
            // If we're moving from RETRIEVE_DATA to SEND_DATA for access requests, create the zip file
            let generatedAccessZip: File | undefined;
            if (isAccessRequestRetrieveDataStep(request)) {
              // Locks the step (and the attachment list) until the zip is built, so attachments
              // can't be deleted while the zip loop is downloading them.
              setIsGeneratingAccessZip(true);
              try {
                generatedAccessZip = await handleAccessRequestNextStep({
                  request,
                  org,
                  attachments,
                  personalIdentifiers,
                  setGeneratedZip,
                  attachment,
                });
              } catch {
                // Continue with forwardStep even if zip creation fails
              } finally {
                setIsGeneratingAccessZip(false);
              }
            }

            if (autoSendRequestEmailNotification) {
              setIsAutoSendAndClosingInProgress(true);
              try {
                if (request.dataRequestType.workflowType === "DELETE") {
                  await autoSendNotificationsAndCloseDeleteRequest(
                    request,
                    org,
                    vendors,
                    dataMap,
                    sellingAndSharingSurveyAnswers,
                    managedAdNetworks,
                    contactVendors,
                    contactRequestor,
                    forwardStep,
                  );
                } else if (request.dataRequestType.workflowType === "OPT_OUT") {
                  await autoSendNotificationsAndCloseOptOutRequest(
                    request,
                    org,
                    vendors,
                    dataMap,
                    sellingAndSharingSurveyAnswers,
                    managedAdNetworks,
                    contactVendors,
                    forwardStep,
                  );
                } else if (request.dataRequestType.workflowType === "LIMIT") {
                  await autoSendNotificationAndCloseLimitRequest(
                    request,
                    org,
                    vendors,
                    dataMap,
                    contactVendors,
                    forwardStep,
                  );
                } else if (
                  request.dataRequestType.workflowType === "KNOW" ||
                  request.dataRequestType.workflowType === "PORTABILITY"
                ) {
                  await autoSendNotificationsAndCloseAccessRequest(
                    contactRequestor,
                    forwardStep,
                    generatedAccessZip,
                  );
                }
                navigateToInbox();
              } finally {
                setIsAutoSendAndClosingInProgress(false);
              }
            } else if (sendAndClose) {
              await contactRequestor({
                closeAfterSend: true,
                currentRequestState: request.state,
              });
              navigateToInbox();
            } else if (lastStep) {
              await forwardStep();
              navigateToInbox();
            } else {
              forwardStep();
            }
          }}
        />
      }
      content={<ProgressFooterSaveNotification request={updateRequests} />}
    />
  );
};

function shouldAutoSendRequestEmailNotifications(
  request: DataSubjectRequestDetailDto,
  org: OrganizationDto,
  vendors: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  managedAdNetworksReady: boolean,
  activeStep: RequestStep,
) {
  if (
    request?.dataRequestType?.workflowType === "KNOW" ||
    request?.dataRequestType?.workflowType === "PORTABILITY"
  ) {
    // Access requests auto-send from the "retrieve data" step: once the data is retrieved we
    // forward to "send data" and email the consumer notification with the data attached, then
    // close. There are no vendor emails to send for access requests, so the only gate is the
    // org-level toggle. (The Next button is already disabled until the step is complete.)
    if (activeStep?.name !== "retrieve data") {
      return false;
    }
    return org?.autoSendRequestEmailNotificationsForAccessRequests === true;
  }

  if (request?.dataRequestType?.workflowType === "DELETE") {
    if (!managedAdNetworksReady) {
      return false;
    }
    if (activeStep?.name !== "delete info") {
      return false;
    }
    if (org?.autoSendRequestEmailNotificationsForDeleteRequests !== true) {
      return false;
    }

    let [filteredVendors] = eligibleVendorsToContactForDeleteRequest(
      vendors,
      request,
      dataMap,
      sellingAndSharingAnswers,
      managedAdNetworks,
      org,
    );
    let [vendorsToContact] = vendorsToContactForDeleteRequest(filteredVendors, request);
    let contactVendorsWithoutEmail = vendorsToContact.filter((v) => !v.email && !v.defaultEmail);
    return contactVendorsWithoutEmail.length == 0;
  }

  if (request?.dataRequestType?.workflowType === "OPT_OUT") {
    if (!managedAdNetworksReady) {
      return false;
    }
    if (activeStep?.name !== "remove consumer") {
      return false;
    }
    if (org?.autoSendRequestEmailNotificationsForOptOutRequests !== true) {
      return false;
    }

    let filteredVendors = filterVendorsForOptOutNotifyThirdPartiesStep(
      vendors,
      request,
      dataMap,
      sellingAndSharingAnswers,
      managedAdNetworks,
      org,
    );
    let vendorsToContact = filterVendorsToContactForOptOutRequest(filteredVendors, request);
    let contactVendorsWithoutEmail = vendorsToContact.filter((v) => !v.email && !v.defaultEmail);
    return contactVendorsWithoutEmail.length == 0;
  }

  if (request?.dataRequestType?.workflowType === "LIMIT") {
    if (activeStep?.name !== "limit data") {
      return false;
    }
    if (org?.autoSendRequestEmailNotificationsForLimitRequests !== true) {
      return false;
    }

    const limitRequestInfo = getAllInformationForLimitRequests(vendors, dataMap);

    // Don't use the auto-send flow if the org is still on the legacy way of determining limit vendors
    if (limitRequestInfo.shouldUseLegacyLimitVendors) {
      return false;
    }

    if (!limitRequestInfo.isLimitRequestsEnabled) {
      return false;
    }

    const limitVendors = vendors.filter((v) =>
      limitRequestInfo.vendorIdsForProcessingLimitRequests.includes(v.vendorId),
    );

    let vendorsToContact = filterLimitVendorsToContactForRequest(
      limitVendors,
      request,
      dataMap,
      org,
    );
    let unprocessedVendorsToContact = vendorsToContact.filter(
      (v) => !alreadyProcessedVendorForLimitRequest(v, request),
    );
    let contactVendorsWithoutEmail = unprocessedVendorsToContact.filter(
      (v) => !v.email && !v.defaultEmail,
    );
    return contactVendorsWithoutEmail.length == 0;
  }

  return false;
}

async function autoSendNotificationsAndCloseDeleteRequest(
  request: DataSubjectRequestDetailDto,
  org: OrganizationDto,
  vendors: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  contactVendor: (v: any) => Promise<void>,
  contactRequestor: (v: any) => Promise<void>,
  forwardStep: (overrideState?: DataRequestState) => Promise<void>,
) {
  let [filteredVendors] = eligibleVendorsToContactForDeleteRequest(
    vendors,
    request,
    dataMap,
    sellingAndSharingAnswers,
    managedAdNetworks,
    org,
  );
  let [vendorsToContact] = vendorsToContactForDeleteRequest(filteredVendors, request);

  await forwardStep();
  if (vendorsToContact.length > 0) {
    await contactVendor({
      vendors: vendorsToContact.map((v) => v.vendorId),
    });
  }
  // Pass CONTACT_VENDORS explicitly - after first forwardStep the request is in CONTACT_VENDORS
  // but the closure still has stale state, causing 409 state mismatch
  await forwardStep("CONTACT_VENDORS");
  await contactRequestor({
    closeAfterSend: true,
    currentRequestState: "NOTIFY_CONSUMER",
  });
}

async function autoSendNotificationsAndCloseOptOutRequest(
  request: DataSubjectRequestDetailDto,
  org: OrganizationDto,
  vendors: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  contactVendor: (v: any) => Promise<void>,
  forwardStep: (overrideState?: DataRequestState) => Promise<void>,
) {
  let filteredVendors = filterVendorsForOptOutNotifyThirdPartiesStep(
    vendors,
    request,
    dataMap,
    sellingAndSharingAnswers,
    managedAdNetworks,
    org,
  );
  let vendorsToContact = filterVendorsToContactForOptOutRequest(filteredVendors, request);

  await forwardStep();
  if (vendorsToContact.length > 0) {
    await contactVendor({
      vendors: vendorsToContact.map((v) => v.vendorId),
    });
  }
  // Pass CONTACT_VENDORS explicitly - after first forwardStep the request is in CONTACT_VENDORS
  // but the closure still has stale REMOVE_CONSUMER, causing 409 state mismatch
  await forwardStep("CONTACT_VENDORS");
}

async function autoSendNotificationAndCloseLimitRequest(
  request: DataSubjectRequestDetailDto,
  org: OrganizationDto,
  vendors: OrganizationDataRecipientDto[],
  dataMap: DataMapDto,
  contactVendor: (v: any) => Promise<void>,
  forwardStep: (overrideState?: DataRequestState) => Promise<void>,
) {
  const { vendorIdsForProcessingLimitRequests } = getAllInformationForLimitRequests(
    vendors,
    dataMap,
  );
  let limitVendors = vendors.filter((v) =>
    vendorIdsForProcessingLimitRequests.includes(v.vendorId),
  );
  let vendorsToContact = filterLimitVendorsToContactForRequest(limitVendors, request, dataMap, org);
  let unprocessedVendorsToContact = vendorsToContact.filter(
    (v) => !alreadyProcessedVendorForLimitRequest(v, request),
  );

  await forwardStep();
  if (unprocessedVendorsToContact.length > 0) {
    await contactVendor({
      vendors: unprocessedVendorsToContact.map((v) => v.vendorId),
    });
  }
  // LIMIT goes CONTACT_VENDORS → CLOSED directly (no NOTIFY_CONSUMER step), so the second
  // forwardStep is the close. Pass CONTACT_VENDORS explicitly because the closure still has
  // the stale LIMIT_DATA state, which would otherwise cause a 409 state mismatch.
  await forwardStep("CONTACT_VENDORS");
}

async function autoSendNotificationsAndCloseAccessRequest(
  contactRequestor: (v: any) => Promise<void>,
  forwardStep: (overrideState?: DataRequestState) => Promise<void>,
  generatedZip?: File,
) {
  // The data zip was already generated in nextOnClick (handleAccessRequestNextStep). Move from
  // RETRIEVE_DATA to SEND_DATA, then email the consumer notification with the zip attached and
  // close. Pass SEND_DATA explicitly because the closure still has the stale RETRIEVE_DATA state,
  // which would otherwise cause a 409 state mismatch. Pass the zip explicitly because the
  // attachment context state update from zip generation hasn't flushed into this closure yet.
  await forwardStep();
  await contactRequestor({
    attachments: generatedZip ? [generatedZip] : undefined,
    closeAfterSend: true,
    currentRequestState: "SEND_DATA",
  });
}
